import { useQuery } from '@tanstack/react-query'
import { ApiError, useApiFetch } from '../../shared/api'
import type { HuntReplayDto } from './huntReplayDto'
import { HUNT_REPLAY_QUERY_KEY } from './useHuntReplay'

/**
 * A shared replay: the snapshot a share link names. Needs no entitlement to
 * the hunt — holding the link is the permission, until it expires or the hunt
 * leader lowers the hunt's level, which revokes it.
 */
const NO_AUTOMATIC_RETRY: ReadonlySet<number> = new Set([404, 410, 502, 503])

export function useSharedHuntReplay(shareId: string | undefined) {
  const apiFetch = useApiFetch()

  return useQuery<HuntReplayDto, ApiError>({
    queryKey: [HUNT_REPLAY_QUERY_KEY, 'shared-replay', shareId],
    enabled: shareId !== undefined && shareId !== '',
    queryFn: () => apiFetch<HuntReplayDto>(`/replay-shares/${encodeURIComponent(shareId!)}`),
    // The snapshot never changes, but the response does: the API composes it on
    // every request with the hunt's live level and who has hidden themselves, and
    // sends it `no-store`. So every arrival asks again, and a link revoked or a
    // trail hidden since the last visit takes effect on the next one rather than
    // after a cached copy ages out. Focus does not refetch: a viewer mid-replay
    // is not interrupted.
    staleTime: 0,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    // 404 (unknown) and 410 (expired or revoked) are final. 502 means the archive
    // service is unreachable and 503 that the API cannot serve shares at all
    // right now; hammering either does not help, and the screen offers a retry.
    // Anything else gets one retry.
    retry: (failureCount, error) =>
      !NO_AUTOMATIC_RETRY.has(error.status) && failureCount < 1,
  })
}
